import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { DocumentDetail } from './pages/DocumentDetail';
import { Documents } from './pages/Documents';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { More } from './pages/More';
import { NewDocument } from './pages/NewDocument';
import { Payments } from './pages/Payments';
import { SelectCompany } from './pages/SelectCompany';
import { Shell } from './shell/Shell';

/**
 * Cesty aplikace. Hlavní obrazovky jsou v základním balíčku, ostatní se stahují až při
 * první návštěvě (fáze 15 – `React.lazy`, Vite z nich dělá samostatné chunky).
 */
const page = <K extends string>(
  loader: () => Promise<Record<K, React.ComponentType<never>>>,
  key: K,
) => lazy(async () => ({ default: (await loader())[key] as React.ComponentType }));

const Account = page(() => import('./pages/Account'), 'Account');
const Accountant = page(() => import('./pages/Accountant'), 'Accountant');
const Admin = page(() => import('./pages/Admin'), 'Admin');
const Assistant = page(() => import('./pages/Assistant'), 'Assistant');
const Calendar = page(() => import('./pages/Calendar'), 'Calendar');
const Tasks = page(() => import('./pages/Tasks'), 'Tasks');
const Ads = page(() => import('./pages/Ads'), 'Ads');
const Approvals = page(() => import('./pages/Approvals'), 'Approvals');
const Automation = page(() => import('./pages/Automation'), 'Automation');
const BankAccounts = page(() => import('./pages/BankAccounts'), 'BankAccounts');
const Companies = page(() => import('./pages/Companies'), 'Companies');
const CompanyApiKeys = page(() => import('./pages/CompanyApiKeys'), 'CompanyApiKeys');
const CompanyFeatures = page(() => import('./pages/CompanyFeatures'), 'CompanyFeatures');
const CompanyMembers = page(() => import('./pages/CompanyMembers'), 'CompanyMembers');
const CompanySecurity = page(() => import('./pages/CompanySecurity'), 'CompanySecurity');
const ContactDetail = page(() => import('./pages/ContactDetail'), 'ContactDetail');
const Contacts = page(() => import('./pages/Contacts'), 'Contacts');
const EmailTemplates = page(() => import('./pages/EmailTemplates'), 'EmailTemplates');
const Guides = page(() => import('./pages/Guides'), 'Guides');
const ImportWizard = page(() => import('./pages/ImportWizard'), 'ImportWizard');
const Jobs = page(() => import('./pages/Jobs'), 'Jobs');
const JobNew = page(() => import('./pages/Jobs'), 'JobNew');
const JobDetail = page(() => import('./pages/JobDetail'), 'JobDetail');
const JobPrint = page(() => import('./pages/JobPrint'), 'JobPrint');
const Notifications = page(() => import('./pages/Notifications'), 'Notifications');
const NumberSeriesPage = page(() => import('./pages/NumberSeries'), 'NumberSeriesPage');
const Periods = page(() => import('./pages/Periods'), 'Periods');
const About = page(() => import('./pages/Placeholders'), 'About');
const Pohoda = page(() => import('./pages/Pohoda'), 'Pohoda');
const PriceLists = page(() => import('./pages/PriceLists'), 'PriceLists');
const PurchaseUpload = page(() => import('./pages/PurchaseUpload'), 'PurchaseUpload');
const Rates = page(() => import('./pages/Rates'), 'Rates');
const Reports = page(() => import('./pages/Reports'), 'Reports');
const ScanPage = page(() => import('./pages/ScanPage'), 'ScanPage');
const Settings = page(() => import('./pages/Settings'), 'Settings');
const Stock = page(() => import('./pages/Stock'), 'Stock');
const StockTakes = page(() => import('./pages/StockTakes'), 'StockTakes');
const StockTakeDetail = page(() => import('./pages/StockTakes'), 'StockTakeDetail');
const Wholesale = page(() => import('./pages/Wholesale'), 'Wholesale');
const Warehouses = page(() => import('./pages/Warehouses'), 'Warehouses');
const Webhooks = page(() => import('./pages/Webhooks'), 'Webhooks');
const WebStats = page(() => import('./pages/WebStats'), 'WebStats');
const PreviewLazy = lazy(() => import('./pages/Preview').then((m) => ({ default: m.Preview })));

export function AppRoutes() {
  return (
    <Suspense fallback={<div className="pad muted">Načítám…</div>}>
      <Routes>
        <Route path="/prihlaseni" element={<Login />} />
        <Route path="/firmy" element={<SelectCompany />} />
        <Route element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="/doklady" element={<Documents />} />
          <Route path="/doklady/prijata/nova" element={<PurchaseUpload />} />
          <Route path="/doklady/:id" element={<DocumentDetail />} />
          <Route path="/doklady/:id/nahled" element={<PreviewLazy mode="document" />} />
          <Route path="/novy" element={<NewDocument />} />
          <Route path="/platby" element={<Payments />} />
          <Route path="/zakazky" element={<Jobs />} />
          <Route path="/zakazky/nova" element={<JobNew />} />
          <Route path="/zakazky/:id" element={<JobDetail />} />
          <Route path="/zakazky/:id/tisk" element={<JobPrint />} />
          <Route path="/asistent" element={<Assistant />} />
          <Route path="/kalendar" element={<Calendar />} />
          <Route path="/ukoly" element={<Tasks />} />
          <Route path="/reklamy" element={<Ads />} />
          <Route path="/prehledy" element={<Reports />} />
          <Route path="/weby" element={<WebStats />} />
          <Route path="/sklad" element={<Stock />} />
          <Route path="/velkoobchod" element={<Wholesale />} />
          <Route path="/sklad/skener" element={<ScanPage />} />
          <Route path="/sklad/inventura" element={<StockTakes />} />
          <Route path="/sklad/inventura/:id" element={<StockTakeDetail />} />
          <Route path="/vice" element={<More />} />
          <Route path="/vice/ucet" element={<Account />} />
          <Route path="/vice/notifikace" element={<Notifications />} />
          <Route path="/vice/schvalovani" element={<Approvals />} />
          <Route path="/vice/automatizace" element={<Automation />} />
          <Route path="/vice/firmy/:id/emaily" element={<EmailTemplates />} />
          <Route path="/vice/firmy/:id/zabezpeceni" element={<CompanySecurity />} />
          <Route path="/vice/admin" element={<Admin />} />
          <Route path="/vice/kontakty" element={<Contacts />} />
          <Route path="/vice/kontakty/:id" element={<ContactDetail />} />
          <Route path="/vice/firmy" element={<Companies />} />
          <Route path="/vice/firmy/:id/vzhled" element={<PreviewLazy mode="company" />} />
          <Route path="/vice/firmy/:id/uzivatele" element={<CompanyMembers />} />
          <Route path="/vice/firmy/:id/api-klice" element={<CompanyApiKeys />} />
          <Route path="/vice/firmy/:id/funkce" element={<CompanyFeatures />} />
          <Route path="/vice/firmy/:id/ucty" element={<BankAccounts />} />
          <Route path="/vice/firmy/:id/webhooky" element={<Webhooks />} />
          <Route path="/vice/import" element={<ImportWizard />} />
          <Route path="/vice/kurzy" element={<Rates />} />
          <Route path="/vice/ucetni" element={<Accountant />} />
          <Route path="/vice/obdobi" element={<Periods />} />
          <Route path="/vice/nastaveni" element={<Settings />} />
          <Route path="/vice/ceniky" element={<PriceLists />} />
          <Route path="/vice/sklady" element={<Warehouses />} />
          <Route path="/vice/rady" element={<NumberSeriesPage />} />
          <Route path="/vice/pohoda" element={<Pohoda />} />
          <Route path="/vice/o-aplikaci" element={<About />} />
          <Route path="/vice/navody" element={<Guides />} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
